import { useHistory } from "react-router-dom";
import { Button, Tabs, Space, Tag, message } from "antd";
import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import invariant from "ts-invariant";
import sdk from "@/sdk";
import { useCanEdit } from "@/hooks/usePermission";

import { GroupDetailsParams } from "../../../pages/user/group-details";
import { TabPane } from "../../../pages/Order";

import { useSearchParam } from "@/utils/url-state";

import { KycTab } from "./tabs";
import { UserTab } from "./tabs/user";
import { PortfolioTab } from "./tabs/portfolio";
import { BalanceHistoryTab } from "./tabs/balanceHistory";
import { WithdrawalTab } from "./tabs/withdrawal";
import { UserContractsTab } from "./tabs/userContracts";
import { HuulgaTab } from "./tabs/huulga";
import { HoldReleaseTab } from "./tabs/holdRelease";

export const GroupDetailsWidget = ({
  _id,
}: {
  _id: GroupDetailsParams["_id"];
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [tab, { set }] = useSearchParam("tab");
  const canEdit = useCanEdit("users-list");

  const { data: userData, refetch: refetchUser } = useQuery({
    queryKey: ["user", _id],
    queryFn: () => {
      invariant(_id, "_id must be defined");
      return sdk.getUser({ _id });
    },
    select: (data) => data.getUser,
    enabled: !!_id,
  });

  const updateFrozenStatus = (isFrozen: boolean) => {
    queryClient.setQueryData(["user", _id], (old: any) => {
      if (!old) return old;
      return {
        ...old,
        getUser: { ...old.getUser, isFrozen },
      };
    });
    queryClient.invalidateQueries(["users"]);
  };

  const { mutate: freezeUser, isLoading: isFreezing } = useMutation({
    mutationFn: () => sdk.freezeUser({ _id }),
    onSuccess: () => {
      message.success("Хэрэглэгч хөлдөөгдлөө.");
      updateFrozenStatus(true);
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message),
  });

  const { mutate: unfreezeUser, isLoading: isUnfreezing } = useMutation({
    mutationFn: () => sdk.unfreezeUser({ _id }),
    onSuccess: () => {
      message.success("Хэрэглэгчийн хөлдөөлт цуцлагдлаа.");
      updateFrozenStatus(false);
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message),
  });

  const { mutate: exportExcel, isLoading: isExportingExcel } = useMutation({
    mutationFn: async () => {
      const { exportUserDetailedReportV2 } =
        await sdk.exportUserDetailedReportV2({ userId: _id });
      if (exportUserDetailedReportV2?.reportUrl) {
        window.open(exportUserDetailedReportV2.reportUrl, "_blank");
      }
    },
  });

  const { mutate: exportPdf, isLoading: isExportingPdf } = useMutation({
    mutationFn: async () => {
      const { exportClientReport } = await sdk.exportClientReport({
        userId: _id,
      });
      if (exportClientReport?.reportUrl) {
        window.open(exportClientReport.reportUrl, "_blank");
      }
    },
  });

  return (
    <>
      <div className="page-header">
        <h3>
          Дэлгэрэнгүй мэдээлэл
          {userData?.isFrozen && (
            <Tag
              color="blue"
              icon={<LockOutlined />}
              style={{ marginLeft: 8, verticalAlign: "middle" }}
            >
              Хөлдсөн
            </Tag>
          )}
        </h3>
        <div className="page-header-buttons">
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              loading={isExportingExcel}
              onClick={() => exportExcel()}
            >
              Excel тайлан
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              loading={isExportingPdf}
              onClick={() => exportPdf()}
            >
              PDF тайлан
            </Button>
            {canEdit &&
              (userData?.isFrozen ? (
                <Button
                  icon={<LockOutlined />}
                  loading={isUnfreezing}
                  onClick={() => unfreezeUser()}
                >
                  Хөлдөөлт цуцлах
                </Button>
              ) : (
                <Button
                  icon={<LockOutlined />}
                  danger
                  loading={isFreezing}
                  onClick={() => freezeUser()}
                >
                  Хөлдөөх
                </Button>
              ))}
            <Button
              icon={<ArrowLeftOutlined />}
              className="back-button"
              onClick={() => history.goBack()}
            >
              Буцах
            </Button>
          </Space>
        </div>
      </div>
      <div className="page-body">
        <Tabs defaultActiveKey={tab ?? "kyc"} onChange={set}>
          <TabPane tab="KYC" key="kyc">
            <KycTab _id={_id} />
          </TabPane>
          <TabPane tab="Хэрэглэгчийн мэдээлэл" key="info">
            <UserTab _id={_id} />
          </TabPane>
          <TabPane tab="Багцын мэдээлэл" key="portfolio">
            <PortfolioTab _id={_id} />
          </TabPane>
          <TabPane tab="Захиалгын биелэлт" key="orders">
            <BalanceHistoryTab _id={_id} />
          </TabPane>
          <TabPane tab="Буцаалт" key="withdrawal">
            <WithdrawalTab _id={_id} />
          </TabPane>
          <TabPane tab="Гэрээ" key="contracts">
            <UserContractsTab _id={_id} />
          </TabPane>
          <TabPane tab="Битүүмж чөлөөлөлт" key="hold-release">
            <HoldReleaseTab _id={_id} />
          </TabPane>
          <TabPane tab="Гүйлгээний түүх" key="huulga">
            <HuulgaTab email={userData?.email} />
          </TabPane>
        </Tabs>
      </div>
    </>
  );
};
